import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, CreditCard, Wallet, Building2, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const PROVIDERS = [
  { type: "stripe", label: "Stripe", icon: CreditCard, kind: "online", desc: "Online card payments. Test key already configured.", fields: [
    { key: "secret_key", label: "Stripe Secret Key (sk_…)", type: "password", placeholder: "sk_live_…" },
    { key: "publishable_key", label: "Publishable Key (pk_…)", placeholder: "pk_live_…", public: true },
  ]},
  { type: "square", label: "Square", icon: Wallet, kind: "online", desc: "Card, Apple Pay, Google Pay, Cash App Pay — all in one SDK.", fields: [
    { key: "access_token", label: "Access Token", type: "password" },
    { key: "application_id", label: "Application ID", public: true },
    { key: "location_id", label: "Location ID" },
    { key: "environment", label: "Environment (sandbox/production)", placeholder: "sandbox" },
  ]},
  { type: "paypal", label: "PayPal (incl. Venmo)", icon: Wallet, kind: "online", desc: "PayPal Checkout — also offers Venmo where supported.", fields: [
    { key: "client_id", label: "Client ID", public: true },
    { key: "client_secret", label: "Client Secret", type: "password" },
    { key: "environment", label: "Environment (sandbox/production)", placeholder: "sandbox" },
  ]},
  { type: "zelle", label: "Zelle", icon: Building2, kind: "manual", desc: "Customer pays manually. We collect the Zelle email/phone you want them to send to.", fields: [
    { key: "handle", label: "Zelle email or phone", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
  { type: "chime", label: "Chime", icon: Building2, kind: "manual", desc: "Manual transfer via Chime $cashtag.", fields: [
    { key: "handle", label: "Chime $cashtag", placeholder: "$YourTag", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
  { type: "cash_app", label: "Cash App", icon: Smartphone, kind: "manual", desc: "Manual payment to your Cash App $cashtag.", fields: [
    { key: "handle", label: "Cash App $cashtag", placeholder: "$YourTag", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
  { type: "venmo", label: "Venmo (manual)", icon: Smartphone, kind: "manual", desc: "Manual payment via Venmo handle.", fields: [
    { key: "handle", label: "Venmo @username", placeholder: "@your-handle", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
  { type: "apple_pay", label: "Apple Pay (manual)", icon: Smartphone, kind: "manual", desc: "Manual instructions if you want customers to send via Apple Cash.", fields: [
    { key: "handle", label: "Apple Cash phone/email", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
  { type: "google_pay", label: "Google Pay (manual)", icon: Smartphone, kind: "manual", desc: "Manual instructions for Google Pay.", fields: [
    { key: "handle", label: "Google Pay handle", isHandle: true },
    { key: "instructions", label: "Extra instructions (optional)", textarea: true, isInstructions: true },
  ]},
];

const STEPS = ["Business Profile", "Notifications", "Payment Methods", "Review"];

export default function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [openDialog, setOpenDialog] = useState(null); // provider type or null

  useEffect(() => {
    api.get("/settings/admin").then((r) => {
      const doc = r.data;
      // Ensure providers array contains all known types
      const existing = new Map((doc.providers || []).map((p) => [p.provider_type, p]));
      const providers = PROVIDERS.map((meta) => existing.get(meta.type) || {
        provider_type: meta.type, enabled: false, display_name: meta.label, public_config: {}, secret_config: {}, handle: "", instructions: "",
      });
      setSettings({ ...doc, providers });
    });
  }, []);

  if (!settings) return <div className="text-slate-500">Loading…</div>;

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const updateProvider = (type, patch) => setSettings((s) => ({
    ...s,
    providers: s.providers.map((p) => p.provider_type === type ? { ...p, ...patch } : p),
  }));

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const payload = {
        store_name: settings.store_name,
        tagline: settings.tagline,
        contact_email: settings.contact_email,
        contact_phone: settings.contact_phone,
        notification_email: settings.notification_email,
        notification_phone: settings.notification_phone,
        address: settings.address,
        about: settings.about,
        providers: settings.providers,
        ...extra,
      };
      await api.patch("/settings/admin", payload);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return false;
    } finally { setBusy(false); }
  };

  const next = async () => {
    const ok = await save();
    if (ok && step < STEPS.length - 1) setStep(step + 1);
  };

  const finish = async () => {
    const ok = await save({ wizard_completed: true });
    if (ok) {
      toast.success("Setup complete!");
      navigate("/dashboard");
    }
  };

  const enabled = settings.providers.filter((p) => p.enabled);
  const meta = (t) => PROVIDERS.find((p) => p.type === t);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Setup Wizard</h1>
          <p className="mt-1 text-sm text-slate-500">Configure your business profile, notifications and payment options.</p>
        </div>
        <div className="text-sm text-slate-500">Step {step + 1} / {STEPS.length}</div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${i === step ? "bg-blue-600 text-white" : i < step ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-500"}`} data-testid={`wizard-step-${i}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <div className="mt-6 dash-surface p-6 md:p-8">
        {step === 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div><Label>Business name</Label><Input className="mt-1" value={settings.store_name || ""} onChange={(e) => update("store_name", e.target.value)} data-testid="wizard-store-name-input" /></div>
              <div><Label>Tagline</Label><Input className="mt-1" value={settings.tagline || ""} onChange={(e) => update("tagline", e.target.value)} data-testid="wizard-tagline-input" /></div>
              <div><Label>Public phone</Label><Input className="mt-1" value={settings.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} data-testid="wizard-public-phone-input" /></div>
              <div><Label>Public email</Label><Input className="mt-1" type="email" value={settings.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} data-testid="wizard-public-email-input" /></div>
              <div className="md:col-span-2"><Label>Address / service area</Label><Input className="mt-1" value={settings.address || ""} onChange={(e) => update("address", e.target.value)} data-testid="wizard-address-input" /></div>
              <div className="md:col-span-2"><Label>About / story</Label><Textarea className="mt-1" rows={4} value={settings.about || ""} onChange={(e) => update("about", e.target.value)} data-testid="wizard-about-textarea" /></div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Where should we send new quote/order notifications? You can change these any time.</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Notification email</Label>
                <Input className="mt-1" type="email" value={settings.notification_email || ""} onChange={(e) => update("notification_email", e.target.value)} placeholder="owner@example.com" data-testid="wizard-notif-email-input" />
              </div>
              <div>
                <Label>Notification SMS phone (E.164, e.g. +14234933600)</Label>
                <Input className="mt-1" value={settings.notification_phone || ""} onChange={(e) => update("notification_phone", e.target.value)} placeholder="+14234933600" data-testid="wizard-notif-phone-input" />
              </div>
            </div>
            <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-900">
              Email uses Resend; SMS uses Twilio. If keys aren't set in <code>backend/.env</code> the notification is logged but the quote still saves.
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-slate-600">Tap a payment option to configure it. Toggle the switch to enable; tap Save to close.</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {settings.providers.map((p) => {
                const m = meta(p.provider_type); if (!m) return null;
                const Icon = m.icon;
                return (
                  <button
                    key={p.provider_type}
                    type="button"
                    onClick={() => setOpenDialog(p.provider_type)}
                    className={`group relative flex w-full items-start gap-3 rounded-md border p-4 text-left transition-colors ${p.enabled ? "border-blue-500 bg-blue-50/40" : "border-slate-200 bg-white hover:border-blue-300"}`}
                    data-testid={`wizard-provider-${p.provider_type}-button`}
                  >
                    <Icon className={`mt-0.5 h-5 w-5 ${p.enabled ? "text-blue-700" : "text-slate-500"}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-slate-900">{m.label}</div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${p.enabled ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>
                          {p.enabled ? "Enabled" : m.kind === "manual" ? "Manual" : "Online"}
                        </span>
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-slate-600">{m.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {settings.providers.map((p) => {
              const m = meta(p.provider_type); if (!m) return null;
              return (
                <Dialog key={p.provider_type} open={openDialog === p.provider_type} onOpenChange={(o) => setOpenDialog(o ? p.provider_type : null)}>
                  <DialogContent className="max-h-[85vh] max-w-md overflow-hidden p-0">
                    <DialogHeader className="border-b border-slate-200 p-5">
                      <DialogTitle className="font-display flex items-center gap-2 text-lg">
                        <m.icon className="h-5 w-5 text-blue-700" /> {m.label}
                      </DialogTitle>
                      <DialogDescription>{m.desc}</DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[55vh] px-5">
                      <div className="space-y-4 py-5">
                        <div className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-3">
                          <div>
                            <Label htmlFor={`switch-${p.provider_type}`}>Enable {m.label}</Label>
                            <div className="text-xs text-slate-500">Show this option at checkout.</div>
                          </div>
                          <Switch
                            id={`switch-${p.provider_type}`}
                            checked={!!p.enabled}
                            onCheckedChange={(v) => updateProvider(p.provider_type, { enabled: v })}
                            data-testid={`wizard-provider-${p.provider_type}-switch`}
                          />
                        </div>
                        <div>
                          <Label>Display name</Label>
                          <Input className="mt-1" value={p.display_name || ""} onChange={(e) => updateProvider(p.provider_type, { display_name: e.target.value })} data-testid={`wizard-provider-${p.provider_type}-display-input`} />
                        </div>
                        {m.fields.map((f) => {
                          const isHandle = f.isHandle;
                          const isInstructions = f.isInstructions;
                          const value = isHandle ? (p.handle || "") : isInstructions ? (p.instructions || "") :
                            (f.public ? (p.public_config?.[f.key] || "") : (p.secret_config?.[f.key] || ""));
                          const onChange = (val) => {
                            if (isHandle) return updateProvider(p.provider_type, { handle: val });
                            if (isInstructions) return updateProvider(p.provider_type, { instructions: val });
                            if (f.public) return updateProvider(p.provider_type, { public_config: { ...(p.public_config || {}), [f.key]: val } });
                            return updateProvider(p.provider_type, { secret_config: { ...(p.secret_config || {}), [f.key]: val } });
                          };
                          return (
                            <div key={f.key}>
                              <Label>{f.label}</Label>
                              {f.textarea ? (
                                <Textarea className="mt-1" rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} data-testid={`wizard-provider-${p.provider_type}-${f.key}-input`} />
                              ) : (
                                <Input className="mt-1" type={f.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} data-testid={`wizard-provider-${p.provider_type}-${f.key}-input`} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                    <DialogFooter className="border-t border-slate-200 p-4">
                      <Button variant="outline" onClick={() => setOpenDialog(null)} data-testid={`wizard-provider-${p.provider_type}-cancel`}>Cancel</Button>
                      <Button
                        onClick={async () => {
                          const ok = await save();
                          if (ok) { setOpenDialog(null); toast.success(`${m.label} saved`); }
                        }}
                        disabled={busy}
                        data-testid={`wizard-provider-${p.provider_type}-save`}
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="font-display text-xl font-bold">Review</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Section title="Business">
                <Row k="Name" v={settings.store_name} />
                <Row k="Tagline" v={settings.tagline} />
                <Row k="Public phone" v={settings.contact_phone} />
                <Row k="Public email" v={settings.contact_email} />
                <Row k="Address" v={settings.address} />
              </Section>
              <Section title="Notifications">
                <Row k="Email" v={settings.notification_email || "—"} />
                <Row k="SMS" v={settings.notification_phone || "—"} />
              </Section>
              <Section title="Enabled payment methods">
                {enabled.length === 0 ? <div className="text-sm text-slate-500">None enabled yet</div> :
                  enabled.map((p) => (
                    <Row key={p.provider_type} k={meta(p.provider_type)?.label} v={p.handle || p.public_config?.client_id || p.public_config?.publishable_key || "configured"} />
                  ))
                }
              </Section>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="outline" disabled={step === 0 || busy} onClick={() => setStep(step - 1)} data-testid="wizard-back-button">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={busy} data-testid="wizard-next-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={busy} data-testid="wizard-finish-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="dash-surface p-5">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</div>
      <div className="mt-2 space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) { return (<div className="flex justify-between gap-4 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-900 text-right">{v || "—"}</span></div>); }
